// src/pages/ResetPasswordPage.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { toast } from '../components/Toast';
import { Lock, Eye, EyeOff, Loader2, Check, X, AlertCircle, Shield, Mail } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';

const inputBase =
  "w-full rounded-lg border px-3 py-2.5 text-sm " +
  "bg-white text-zinc-900 border-zinc-300 placeholder-zinc-400 " +
  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 " +
  "dark:bg-zinc-950 dark:text-zinc-100 dark:border-zinc-800 dark:placeholder-zinc-500";

const btnPrimary =
  "w-full flex items-center justify-center gap-2 rounded-lg border border-blue-700 " +
  "bg-blue-600 px-4 py-2.5 text-sm font-medium text-white " +
  "hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 " +
  "disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [isRecoverySession, setIsRecoverySession] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const hasProcessedHash = useRef(false);

  // CAPTCHA state
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Set page title
  useEffect(() => { document.title = "Reset Password | Nexus"; }, []);

  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);

  // Password requirement checks
  const passwordChecks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };
  const allPasswordChecksPassed = Object.values(passwordChecks).every(Boolean);

  // Check if user has MFA enabled and needs to verify
  const checkMfaStatus = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) return;

      // If currentLevel is aal1 and nextLevel is aal2, user needs MFA verification
      if (data?.currentLevel === 'aal1' && data?.nextLevel === 'aal2') {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totpFactor = factors?.totp?.find(f => f.status === 'verified');
        if (totpFactor) {
          setMfaRequired(true);
          setMfaFactorId(totpFactor.id);
        }
      } else if (data?.currentLevel === 'aal2') {
        // Already at AAL2, no MFA needed
        setMfaVerified(true);
      }
    } catch (err) {
      // Ignore errors - proceed without MFA check
    }
  };

  // Verify MFA code
  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (!mfaFactorId || mfaCode.length !== 6) return;

    setMfaVerifying(true);
    setError('');

    try {
      // Create challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId
      });
      if (challengeError) throw challengeError;

      // Verify the code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challengeData.id,
        code: mfaCode
      });
      if (verifyError) throw verifyError;

      setMfaVerified(true);
      setMfaRequired(false);
      toast.success('MFA verified! You can now reset your password.');
    } catch (err) {
      setError(err.message || 'Invalid verification code');
      setMfaCode('');
    } finally {
      setMfaVerifying(false);
    }
  };

  // Handle the recovery token and auth state
  useEffect(() => {
    if (hasProcessedHash.current) return;
    hasProcessedHash.current = true;

    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const errorCode = hashParams.get('error_code');
    const errorDescription = hashParams.get('error_description');
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');

    // Check for errors first
    if (errorCode) {
      if (errorCode === 'otp_expired') {
        setTokenError('This password reset link has expired. Please request a new one.');
      } else if (errorCode === 'access_denied') {
        setTokenError(errorDescription?.replace(/\+/g, ' ') || 'Access denied. Please request a new reset link.');
      } else {
        setTokenError(errorDescription?.replace(/\+/g, ' ') || 'Invalid reset link. Please request a new one.');
      }
      setInitializing(false);
      return;
    }

    // Helper to complete initialization with session
    const completeInit = async (session) => {
      setIsRecoverySession(true);
      setUserEmail(session.user?.email || '');
      await checkMfaStatus();
      setInitializing(false);
      // Clear the hash from URL after session is established
      window.history.replaceState(null, '', window.location.pathname);
    };

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        if (session) {
          await completeInit(session);
        }
      }
    });

    // If we have recovery tokens in URL, manually trigger session exchange
    // This fixes the "stuck on verifying" issue
    if (accessToken && type === 'recovery') {
      // Force Supabase to process the hash by calling getSession
      // This triggers the token exchange if it hasn't happened yet
      const processRecoveryToken = async () => {
        try {
          // First check if there's already a session (hash was auto-processed)
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            await completeInit(session);
            return;
          }

          // If no session yet, the auth state change listener will handle it
          // But set a timeout to handle edge cases
          setTimeout(async () => {
            const { data: { session: retrySession } } = await supabase.auth.getSession();
            if (retrySession) {
              await completeInit(retrySession);
            } else if (initializing) {
              setTokenError('Failed to verify reset link. Please request a new one.');
              setInitializing(false);
            }
          }, 3000);
        } catch (err) {
          console.error('Error processing recovery token:', err);
          setTokenError('Failed to verify reset link. Please try again.');
          setInitializing(false);
        }
      };

      processRecoveryToken();

      return () => {
        subscription.unsubscribe();
      };
    }

    // No hash tokens - check if we already have a session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // User has a session (either from recovery link or already logged in)
        await completeInit(session);
      } else {
        // No session and no recovery tokens - show error
        setTokenError('No valid reset session found. Please request a new password reset link.');
        setInitializing(false);
      }
    };

    checkSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allPasswordChecksPassed) {
      setError('Please meet all password requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Verify CAPTCHA
    if (turnstileSiteKey && !captchaToken) {
      setError('Please complete the security verification');
      return;
    }

    // Verify we have a valid session before attempting update
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setError('Session expired. Please request a new password reset link.');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) throw updateError;

      setSuccess(true);
      toast.success('Password updated successfully!');

      // Sign out to clear the recovery session, then redirect to login
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = '/';
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestNewLink = async () => {
    // Sign out any existing session before going to login
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  // Show loading while initializing
  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-sm text-zinc-600 dark:text-zinc-400">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Show error state if token is invalid/expired
  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-8 shadow-sm">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
                Reset Link Invalid
              </h1>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6">
                {tokenError}
              </p>
              <button onClick={handleRequestNewLink} className={btnPrimary}>
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-8 shadow-sm">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
                Password Updated!
              </h1>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Redirecting you to the dashboard...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show MFA verification form if required
  if (mfaRequired && !mfaVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-8 shadow-sm">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                Two-Factor Authentication
              </h1>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                Enter the 6-digit code from your authenticator app to continue
              </p>
            </div>

            <form onSubmit={handleMfaVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                  Verification Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  className={`${inputBase} text-center text-2xl tracking-[0.5em] font-mono`}
                  placeholder="000000"
                  autoFocus
                  required
                />
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={mfaVerifying || mfaCode.length !== 6}
                className={btnPrimary}
              >
                {mfaVerifying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Continue'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={handleRequestNewLink}
                className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-8 shadow-sm">
          <div className="text-center mb-6">
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
              Reset Your Password
            </h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              Enter your new password below
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Display (Read-only) */}
            {userEmail && (
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                  Account
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input
                    type="email"
                    value={userEmail}
                    readOnly
                    className={`${inputBase} pl-10 bg-zinc-50 dark:bg-zinc-900 cursor-not-allowed`}
                  />
                </div>
              </div>
            )}

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputBase} pl-10 pr-10`}
                  placeholder="Enter new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            {password && (
              <div className="space-y-1.5 text-xs">
                {[
                  { key: 'minLength', label: 'At least 8 characters' },
                  { key: 'hasUppercase', label: 'One uppercase letter' },
                  { key: 'hasLowercase', label: 'One lowercase letter' },
                  { key: 'hasNumber', label: 'One number' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    {passwordChecks[key] ? (
                      <Check className="w-3.5 h-3.5 text-green-500" />
                    ) : (
                      <X className="w-3.5 h-3.5 text-zinc-400" />
                    )}
                    <span className={passwordChecks[key] ? 'text-green-600 dark:text-green-400' : 'text-zinc-500'}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`${inputBase} pl-10`}
                  placeholder="Confirm new password"
                  required
                />
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
              )}
            </div>

            {/* Cloudflare Turnstile CAPTCHA */}
            {turnstileSiteKey && (
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  onSuccess={(token) => setCaptchaToken(token)}
                  onError={() => {
                    setCaptchaToken('');
                    setError('Security verification failed. Please try again.');
                  }}
                  onExpire={() => {
                    setCaptchaToken('');
                  }}
                  options={{
                    theme: 'auto',
                    size: 'normal',
                  }}
                />
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !allPasswordChecksPassed || password !== confirmPassword || (turnstileSiteKey && !captchaToken)}
              className={btnPrimary}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={handleRequestNewLink}
              className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
